/**
 * ForgotPassword — /auth/forgot
 *
 * Step 1 of the password recovery flow. Posts email to
 * /api/auth/password/reset/request. Server always returns 200 (anti-
 * enumeration) so we show the same success message regardless of whether
 * the email is registered. The user then clicks the link in their inbox,
 * which lands on `/auth/reset/:token`.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import BloqLogo from "@/components/BloqLogo";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/password/reset/request", { email: email.trim() });
      setSent(true);
    } catch (err) {
      // Backend always returns 200, so this only fires on network errors.
      toast.error(formatApiError(err) || "Could not send reset email");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="bloq-container pt-7">
        <BloqLogo size={26} />
      </div>
      <div className="flex-1 grid place-items-center bloq-container py-12">
        <div className="max-w-md w-full" data-testid="forgot-password-card">
          <p className="bloq-eyebrow mb-3">Account recovery</p>
          <h1 className="text-4xl bloq-headline mb-3">Reset your password</h1>

          {sent ? (
            <div className="bloq-card p-6 mt-6 flex flex-col items-center text-center gap-3" data-testid="forgot-sent-state">
              <Mail className="w-10 h-10 text-[#005e7a]" />
              <p className="font-semibold">Check your inbox</p>
              <p className="text-sm text-[#4a4a52]">
                If an account exists for <strong>{email}</strong>, we&apos;ve sent a password reset
                link. It expires in 1 hour.
              </p>
              <button
                onClick={() => navigate("/login")}
                className="bloq-btn-primary mt-2 px-6 py-2.5"
                data-testid="forgot-back-to-login"
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <>
              <p className="text-[#4a4a52] text-base mb-8">
                Enter the email you signed up with. We&apos;ll send you a link to choose a new password.
              </p>
              <form onSubmit={onSubmit} className="flex flex-col gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bloq-input mt-1"
                    data-testid="forgot-email-input"
                    placeholder="you@company.com"
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  className="bloq-btn-primary mt-3 px-6 py-3"
                  data-testid="forgot-submit-button"
                >
                  {busy ? "Sending…" : "Send reset link"}
                </button>
              </form>
              <Link
                to="/login"
                className="mt-7 inline-flex items-center gap-1.5 text-sm text-[#4a4a52] hover:text-[#1a1a1f]"
                data-testid="forgot-back-link"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
